import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppState } from "../../../Reducers/Reducers";
import { User } from "../../Menu/MaterialTripList/objects/user";

/**
 * Actions the API refuses depending on the user's licence.
 *
 * The rules mirror `get_permissions()` in vat-api: `apps/trip/views.py` for the two
 * trip ones, `apps/booking/views.py` for the booking one, and the `views_cart.py` of
 * every product app for the cart. Those views swap their permission classes for
 * `IsSuperUser` as soon as the caller holds a restricted licence, so the request comes
 * back 403. Checking the same rules here lets the UI refuse up front instead of waiting
 * for that answer.
 *
 * - `TRIP_WRITE` is the trip resource itself (create, duplicate, rename, delete).
 * - `TRIP_CONTENT_WRITE` is everything hanging off a version of an existing trip: its
 *   status and dates, its itinerary, and every cart product.
 * - `BOOKING` is sending products to the suppliers.
 *
 * Assistants are the only licence that tells them apart: they may fill and book an
 * existing trip, they just cannot create one.
 */
export type LicenceRestrictedAction = 'TRIP_WRITE' | 'TRIP_CONTENT_WRITE' | 'BOOKING';

export type LicenceRestriction = {
    allowed: boolean,
    /** Translated reason, `null` when the action is allowed. */
    reason: string | null
};

type LicenceUser = Pick<User, 'is_superuser' | 'is_commercial' | 'is_finance' | 'is_assistant'>;

// One whole sentence per licence rather than an interpolated profile name, so
// translators own the full wording.
const RESTRICTION_MESSAGE_KEYS = {
    commercial: 'licence.restriction.commercial',
    finance: 'licence.restriction.finance',
    assistant: 'licence.restriction.assistant'
};

const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Translation key explaining why `action` is refused, `null` when it is allowed.
 * Use it from plain functions; components should use `useLicenceRestriction`.
 */
export function getLicenceRestrictionMessageKey(
    user: LicenceUser | null | undefined,
    action: LicenceRestrictedAction
): string | null {
    if (!user/*  || user.is_superuser */) {
        return null;
    }

    // Commercial and finance licences are read-only on trips, carts and bookings alike.
    if (user.is_commercial) {
        return RESTRICTION_MESSAGE_KEYS.commercial;
    }

    if (user.is_finance) {
        return RESTRICTION_MESSAGE_KEYS.finance;
    }

    // Assistants keep their cart and booking rights, they only cannot build a trip.
    if (user.is_assistant && action === 'TRIP_WRITE') {
        return RESTRICTION_MESSAGE_KEYS.assistant;
    }

    return null;
}

/**
 * The action a request performs, or `null` when it writes nothing a licence restricts.
 *
 * Everything nested under a trip version — the version itself, the itinerary, the cart
 * products, the booking calls — is `TRIP_CONTENT_WRITE`; a write straight on `/trip/` is
 * the trip resource, which assistants are refused as well.
 */
export function getRequestLicenceAction(
    method: string | undefined,
    url: string
): LicenceRestrictedAction | null {
    if (!WRITE_METHODS.includes((method ?? '').toUpperCase())) {
        return null;
    }

    // Drop the query string: a token or a filter must never decide the action.
    const path = url.split('?')[0] ?? '';

    if (!path.includes('/trip/')) {
        return null;
    }

    return path.includes('/versions/') ?
        'TRIP_CONTENT_WRITE' :
        'TRIP_WRITE';
}

export function useLicenceRestriction(action: LicenceRestrictedAction): LicenceRestriction {
    const user = useSelector((state: AppState) => state.user.user);
    const { t } = useTranslation();

    const messageKey = getLicenceRestrictionMessageKey(user, action);

    return {
        allowed: !messageKey,
        reason: messageKey ? t(messageKey) : null
    };
}
